declare global {
  interface Window {
    Api: Api
  }
}

export class HttpError extends Error {
  public status: number;
  public responseText: string;
  public body: any;

  public constructor(status: number, message: string, body: any) {
    super(`${status}: ${message}`)
    this.status = status
    this.responseText = message
    this.body = body
  }
}

interface SendParams {
  url: string,
  method: 'GET' | 'POST',
  body?: FormData | object,
  init?: RequestInit,
  nonAjaxUrl?: true
}

class Api {
  static async send<T = {
    [key: string]: any
  }>(params: SendParams): Promise<Partial<T>>
  static async send(params: SendParams, blob: true): Promise<Blob>;
  static async send({
    url,
    method,
    body,
    init,
    nonAjaxUrl
  }: SendParams, blob?: true) {

    const realInit: RequestInit = {
      headers: {},
      ...init,
      method,
    }

    let domain = ''
    if (typeof window !== 'undefined') {
      const domainMeta = document.querySelector('meta[name=ajax-domain]')
      if (domainMeta) {
        domain = domainMeta.getAttribute('content')
      }
    } else {
      domain = process.env.AJAX_DOMAIN
    }

    if (!blob) {
      realInit.headers['Accept'] = 'application/json'
    }

    if (process.env.HTTP_COOKIE) {
      realInit.headers['cookie'] = process.env.HTTP_COOKIE
      realInit.headers['from-back'] = 'true'
    }

    if (body) {
      let csrfToken = ''
      const csrfTokenMeta = document.querySelector('meta[name=csrf-token]')
      if (csrfTokenMeta) {
        csrfToken = csrfTokenMeta.getAttribute('content');
      }

      let realBody: FormData | string = !(body instanceof FormData) ? JSON.stringify(body) : body
      if (typeof realBody === 'string') {
        realInit.headers['Content-Type'] = 'application/json;charset=UTF-8'
      }
      realInit.body = realBody
      realInit.headers['X-CSRF-Token'] = csrfToken
    }

    const realUrl = nonAjaxUrl ? `${domain}/${url}` : `${domain}/ajax/${url}`

    const response = await fetch(realUrl, realInit)
      .catch(e => {
        // Если запрос был отменён, то это не ошибка
        if (e.name !== 'AbortError') {
          throw e
        }
      })

    if (!response) {
      // Если запрос был отменён — возвращаем пустой объект
      if (blob) {
        return new Blob()
      }
      return {}
    }

    if (!response.ok) {
      let responseBody = {}
      try {
        responseBody = await response.json()
      } catch (e) {
        // Do nothing
      }
      throw new HttpError(response.status, response.statusText, responseBody)
    }

    if (blob) {
      return await response.blob()
    }
    return await response.json()
  }

  static get<T = {
    [key: string]: any
  }>(params: Omit<SendParams, 'method' | 'body'>): Promise<Partial<T>>;
  static get(params: Omit<SendParams, 'method' | 'body'>, blob: true): Promise<Blob>;
  static get(params: Omit<SendParams, 'method' | 'body'>, blob?: true) {
    return Api.send({
      ...params,
      method: 'GET',
    }, blob)
  }

  static post<T = {
    [key: string]: any
  }>(params: Omit<SendParams, 'method'>): Promise<Partial<T>>
  static post(params: Omit<SendParams, 'method'>, blob: true): Promise<Blob>
  static post(params: Omit<SendParams, 'method'>, blob?: true) {
    return Api.send({
      ...params,
      method: 'POST',
    }, blob)
  }

  static getBlob = (url: string): Promise<Blob> => Api.send({
    url,
    method: 'GET',
  }, true)
}

if (typeof window !== 'undefined') {
  window.Api = Api
}

export default Api
